import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mt-1 text-sm text-red-600"
}
const _hoisted_2 = { class: "space-y-4" }
const _hoisted_3 = { class: "grid grid-cols-6 gap-3" }
const _hoisted_4 = ["onUpdate:modelValue"]
const _hoisted_5 = { class: "flex justify-end space-x-3 pt-4 border-t border-gray-200" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}

import { ref, reactive, watch } from "vue";
import type { GammeComplete, UpdateGammeData } from "@/types";

interface Props {
  gamme: GammeComplete;
}

interface Emits {
  (e: "submit", data: UpdateGammeData): void;
  (e: "cancel"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GammeEditForm',
  props: {
    gamme: {}
  },
  emits: ["submit", "cancel"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const isSubmitting = ref(false);

const formData = reactive<UpdateGammeData>({
  id_declinaison: 0,
  titre: "",
  d1_FR: null,
  d2_FR: null,
  d3_FR: null,
  d4_FR: null,
  d5_FR: null,
  d6_FR: null,
  d7_FR: null,
  d8_FR: null,
  d9_FR: null,
  d10_FR: null,
  d11_FR: null,
  d12_FR: null,
});

const errors = reactive({
  titre: "",
});

// Colonnes de tailles réactives
const sizeColumns = ref<(string | null)[]>([
  formData.d1_FR,
  formData.d2_FR,
  formData.d3_FR,
  formData.d4_FR,
  formData.d5_FR,
  formData.d6_FR,
  formData.d7_FR,
  formData.d8_FR,
  formData.d9_FR,
  formData.d10_FR,
  formData.d11_FR,
  formData.d12_FR,
]);

// Initialiser le formulaire
watch(
  () => props.gamme,
  (newGamme) => {
    if (newGamme) {
      formData.id_declinaison = parseInt(newGamme.id_declinaison.toString());
      formData.titre = newGamme.titre || "";

      // Mettre à jour les colonnes de tailles
      sizeColumns.value = [
        newGamme.d1_FR,
        newGamme.d2_FR,
        newGamme.d3_FR,
        newGamme.d4_FR,
        newGamme.d5_FR,
        newGamme.d6_FR,
        newGamme.d7_FR,
        newGamme.d8_FR,
        newGamme.d9_FR,
        newGamme.d10_FR,
        newGamme.d11_FR,
        newGamme.d12_FR,
      ];
    }
  },
  { immediate: true },
);

// Synchroniser les colonnes avec formData
watch(
  sizeColumns,
  (newColumns) => {
    formData.d1_FR = newColumns[0] || null;
    formData.d2_FR = newColumns[1] || null;
    formData.d3_FR = newColumns[2] || null;
    formData.d4_FR = newColumns[3] || null;
    formData.d5_FR = newColumns[4] || null;
    formData.d6_FR = newColumns[5] || null;
    formData.d7_FR = newColumns[6] || null;
    formData.d8_FR = newColumns[7] || null;
    formData.d9_FR = newColumns[8] || null;
    formData.d10_FR = newColumns[9] || null;
    formData.d11_FR = newColumns[10] || null;
    formData.d12_FR = newColumns[11] || null;
  },
  { deep: true },
);

const validateForm = () => {
  errors.titre = "";

  if (!formData.titre.trim()) {
    errors.titre = "Le nom de la gamme est requis";
    return false;
  }

  return true;
};

const hasAtLeastOneSize = (data: UpdateGammeData) => {
  const sizes = [
    data.d1_FR,
    data.d2_FR,
    data.d3_FR,
    data.d4_FR,
    data.d5_FR,
    data.d6_FR,
    data.d7_FR,
    data.d8_FR,
    data.d9_FR,
    data.d10_FR,
    data.d11_FR,
    data.d12_FR,
  ];
  return sizes.some(
    (s) => s != null && s !== undefined && String(s).trim() !== "",
  );
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  isSubmitting.value = true;

  try {
    let payload: UpdateGammeData = { ...formData };
    if (!hasAtLeastOneSize(payload) && props.gamme) {
      payload = {
        ...payload,
        d1_FR: props.gamme.d1_FR ?? null,
        d2_FR: props.gamme.d2_FR ?? null,
        d3_FR: props.gamme.d3_FR ?? null,
        d4_FR: props.gamme.d4_FR ?? null,
        d5_FR: props.gamme.d5_FR ?? null,
        d6_FR: props.gamme.d6_FR ?? null,
        d7_FR: props.gamme.d7_FR ?? null,
        d8_FR: props.gamme.d8_FR ?? null,
        d9_FR: props.gamme.d9_FR ?? null,
        d10_FR: props.gamme.d10_FR ?? null,
        d11_FR: props.gamme.d11_FR ?? null,
        d12_FR: props.gamme.d12_FR ?? null,
      };
    }
    emit("submit", payload);
  } catch (error) {
    console.error("Erreur lors de la modification:", error);
  } finally {
    isSubmitting.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    onSubmit: _withModifiers(handleSubmit, ["prevent"]),
    class: "space-y-6"
  }, [
    _createElementVNode("div", null, [
      _cache[2] || (_cache[2] = _createElementVNode("label", {
        for: "edit-titre",
        class: "block text-sm font-medium text-gray-700 mb-2"
      }, " Nom de la gamme * ", -1)),
      _withDirectives(_createElementVNode("input", {
        id: "edit-titre",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.titre) = $event)),
        type: "text",
        required: "",
        placeholder: "Ex: T-shirts Homme",
        class: _normalizeClass(["w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500", { 'border-red-500': errors.titre }])
      }, null, 2), [
        [_vModelText, formData.titre]
      ]),
      (errors.titre)
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(errors.titre), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex items-center justify-between" }, [
        _createElementVNode("label", { class: "block text-sm font-medium text-gray-700" }, " Tailles disponibles "),
        _createElementVNode("div", { class: "text-sm text-gray-500" }, "Colonnes d1 à d12")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sizeColumns.value, (size, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "text-center"
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["px-2 py-1 rounded-md text-sm font-medium border", 
              size ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
            ])
            }, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((sizeColumns.value[index]) = $event),
                type: "text",
                placeholder: "-",
                class: "w-full text-center bg-transparent border-none outline-none text-sm"
              }, null, 8, _hoisted_4), [
                [_vModelText, sizeColumns.value[index]]
              ])
            ], 2)
          ]))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("button", {
        type: "button",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('cancel'))),
        class: "px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      }, " Annuler "),
      _createElementVNode("button", {
        type: "submit",
        disabled: isSubmitting.value,
        class: "px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      }, [
        (isSubmitting.value)
          ? (_openBlock(), _createElementBlock("i", _hoisted_7))
          : _createCommentVNode("", true),
        _cache[4] || (_cache[4] = _createTextVNode(" Sauvegarder les modifications "))
      ], 8, _hoisted_6)
    ])
  ], 32))
}
}

})